<template>
  <div
    class="theme_copy_pay"
    :class="[`theme_copy_pay--${themeId}`, `theme_copy_pay--${layout}`]"
  >
    <header class="theme_copy_pay_header">
      <div class="theme_copy_pay_brand">
        <div class="theme_copy_pay_logo">
          <img v-if="logoUrl" :src="logoUrl" alt="" />
        </div>
      </div>
      <img
        v-if="isCustomGold && copyPayHero"
        class="theme_copy_pay_header_hero"
        :src="copyPayHero"
        alt=""
      />
      <img
        v-if="isCustomGold && copyPayCard"
        class="theme_copy_pay_header_card"
        :src="copyPayCard"
        alt=""
      />
    </header>

    <section class="theme_copy_pay_timer">
      <span>{{ $t('text_remaining_time') }}</span>
      <div class="theme_copy_pay_timer_value">
        <strong>{{ displayTime(hr) }}</strong>
        <em>:</em>
        <strong>{{ displayTime(min) }}</strong>
        <em>:</em>
        <strong>{{ displayTime(sec) }}</strong>
      </div>
    </section>

    <main class="theme_copy_pay_body">
      <section class="theme_copy_pay_card theme_copy_pay_payee">
        <div class="theme_copy_pay_card_head">
          <span class="theme_copy_pay_badge">
            <span class="theme_copy_pay_badge_value">01</span>
          </span>
          <div>
            <h2>{{ $t('text_copy_pay') }}</h2>
            <p>
              {{ $t('text_copy_this_bank_name1') }}
              <b v-if="payeeName && payeeName !== countryInfo?.enName">{{ payeeName }}</b>
              {{ $t('text_copy_this_bank_name2') }}
            </p>
          </div>
          <img
            v-if="isSelfBlue && copyPayHero"
            class="theme_copy_pay_payee_hero"
            :src="copyPayHero"
            alt=""
          />
        </div>

        <div class="theme_copy_pay_account_panel">
          <img
            v-if="isCustomGold && copyPayCard"
            class="theme_copy_pay_card_deco"
            :src="copyPayCard"
            alt=""
          />
          <div v-if="isThailand" class="theme_copy_pay_info_row">
            <img
              v-if="copyAmountIcon"
              class="theme_copy_pay_row_icon"
              :src="copyAmountIcon"
              alt=""
            />
            <div class="theme_copy_pay_info_text">
              <span>{{ $t('text_order_amount') }}</span>
              <strong>{{ currencyCode }} {{ cardAmountText }}</strong>
            </div>
          </div>
          <div
            class="theme_copy_pay_amount"
            :class="{ 'theme_copy_pay_amount--copyable': isThailand || canCopyAmount }"
          >
            <img
              v-if="copyAmountIcon"
              class="theme_copy_pay_row_icon"
              :src="copyAmountIcon"
              alt=""
            />
            <div class="theme_copy_pay_amount_text">
              <span>{{ isThailand ? $t('text_payment_amount') : $t('text_total_amount') }}</span>
              <strong>{{ currencyCode }} {{ amountText }}</strong>
            </div>
            <button
              v-if="isThailand || canCopyAmount"
              type="button"
              @click="$emit('copy', amountText)"
            >
              {{ $t('text_copy') }}
            </button>
          </div>
          <div
            v-if="accountName"
            class="theme_copy_pay_info_row theme_copy_pay_info_row--name"
          >
            <img
              v-if="copyNameIcon"
              class="theme_copy_pay_row_icon"
              :src="copyNameIcon"
              alt=""
            />
            <div class="theme_copy_pay_info_text">
              <span>{{ $t('text_account_name') }}</span>
              <strong>{{ accountName }}</strong>
            </div>
            <button
              v-if="canCopyAccountName"
              type="button"
              @click="$emit('copy', accountName)"
            >
              {{ $t('text_copy') }}
            </button>
          </div>
          <div v-if="isThailand" class="theme_copy_pay_info_row">
            <img
              v-if="copyAccountIcon"
              class="theme_copy_pay_row_icon"
              :src="copyAccountIcon"
              alt=""
            />
            <div class="theme_copy_pay_info_text">
              <span>{{ $t('text_bank_name') }}</span>
              <strong>{{ bankAlias }}</strong>
            </div>
          </div>
          <div class="theme_copy_pay_info_row theme_copy_pay_info_row--account">
            <img
              v-if="copyAccountIcon"
              class="theme_copy_pay_row_icon"
              :src="copyAccountIcon"
              alt=""
            />
            <div class="theme_copy_pay_info_text">
              <span>{{ payeeAccountLabel }}</span>
              <strong>{{ accountNumber }}</strong>
            </div>
            <button type="button" @click="$emit('copy', accountNumber)">
              {{ $t('text_copy') }}
            </button>
          </div>
          <div v-if="agentAccountId" class="theme_copy_pay_info_row">
            <img
              v-if="copyAccountIcon"
              class="theme_copy_pay_row_icon"
              :src="copyAccountIcon"
              alt=""
            />
            <div class="theme_copy_pay_info_text">
              <span>{{ $t('text_agent_id') }}</span>
              <strong>{{ agentAccountId }}</strong>
            </div>
            <button type="button" @click="$emit('copy', agentAccountId)">
              {{ $t('text_copy') }}
            </button>
          </div>
        </div>
        <button
          v-if="dialCode"
          class="theme_copy_pay_dial_btn"
          type="button"
          @click="onDial"
        >
          {{ $t('text_click_pay') }}
        </button>
        <div v-if="dialCode" class="theme_copy_pay_dial_code">
          <span>{{ dialCode }}</span>
          <button type="button" @click="$emit('copy', dialCode)">
            {{ $t('text_copy') }}
          </button>
        </div>
      </section>

      <section
        class="theme_copy_pay_card theme_copy_pay_status"
        :class="{ 'is-success': isCollectionSuccess }"
      >
        <div class="theme_copy_pay_card_head">
          <span class="theme_copy_pay_badge">
            <span class="theme_copy_pay_badge_value">02</span>
          </span>
          <div>
            <h2>{{ $t('text_payment_completed') }}</h2>
            <p>{{ statusHelpText }}</p>
          </div>
        </div>

        <div class="theme_copy_pay_paid_panel">
          <span>{{ $t('text_amount_paid') }}:</span>
          <div class="theme_copy_pay_paid_value">
            <strong>{{ currencyCode }} {{ collectedAmountText }}</strong>
            <button
              v-if="!isCollectionSuccess"
              type="button"
              @click="$emit('refresh')"
            >
              {{ $t('text_refresh') }}
            </button>
            <img
              v-else-if="paymentMethodSelectedImg"
              :src="paymentMethodSelectedImg"
              alt=""
            />
          </div>
          <p v-if="!isCollectionSuccess">
            {{ $t('text_expected_payment') }}
          </p>
        </div>

        <div v-if="showSmsInput" class="theme_copy_pay_sms">
          <label>{{ $t('text_get_results_faster') }}</label>
          <p class="theme_copy_pay_sms_desc">{{ $t('text_fill_payment') }}</p>
          <textarea
            :readonly="isSubmitTransactionId"
            :value="transactionIdTemplate"
            :placeholder="smsPlaceholder"
            @input="$emit('update:transaction-id-template', $event.target.value)"
          />
          <div v-if="isSubmitTransactionId" class="theme_copy_pay_sms_result">
            <span>{{ $t('text_transaction_id') }}</span>
            <strong>{{ transactionId }}</strong>
          </div>
          <p v-if="!isSubmitTransactionId" class="theme_copy_pay_sms_warn">
            <span>*</span>{{ $t('text_fill_wrong_payment') }}
          </p>
          <button
            v-if="!isSubmitTransactionId"
            type="button"
            @click="$emit('submit-transaction')"
          >
            {{ $t('text_submit') }}
          </button>
        </div>
      </section>

      <section
        v-if="!isThailand"
        class="theme_copy_pay_card theme_copy_pay_wallet"
      >
        <div class="theme_copy_pay_card_head">
          <span class="theme_copy_pay_badge">
            <span class="theme_copy_pay_badge_value">03</span>
          </span>
          <div>
            <h2>{{ $t('text_your_payment_account') }}</h2>
            <p>{{ userPaymentAccount }}</p>
          </div>
          <div class="theme_copy_pay_wallet_icon">
            <img
              v-if="copyWalletIcon"
              :src="copyWalletIcon"
              alt=""
            />
          </div>
        </div>
      </section>
      <section
        v-if="tipsContent"
        class="theme_copy_pay_card theme_copy_pay_tips"
      >
        <h2>{{ $t('text_payment_reminder') }}</h2>
        <div class="theme_copy_pay_tips_content" v-html="processedTipsContent"></div>
      </section>
    </main>

    <div
      v-if="collectionDetailShow"
      class="theme_copy_pay_overlay"
    >
      <section class="theme_copy_pay_modal theme_copy_pay_detail_modal">
        <h3>{{ $t('text_collection_details') }}</h3>
        <div class="theme_copy_pay_detail_table">
          <div class="theme_copy_pay_detail_table_row theme_copy_pay_detail_table_head">
            <span>{{ $t('text_order_number') }}</span>
            <span>{{ $t('text_debit_account') }}</span>
            <span>{{ $t('text_remittance_amount') }}</span>
            <span>{{ $t('text_payment_time') }}</span>
          </div>
          <div
            v-for="(item, index) in collectDetails"
            :key="index"
            class="theme_copy_pay_detail_table_row"
          >
            <span>{{ item.bankTransactionReference }}</span>
            <span>{{ item.destinationAccountNumber }}</span>
            <span>{{ item.amount }}</span>
            <span>{{ item.transactionTime }}</span>
          </div>
        </div>
        <button type="button" @click="$emit('close-collection-detail')">
          {{ $t('text_confirm2') }}
        </button>
      </section>
    </div>

    <div
      v-else-if="collectionShow"
      class="theme_copy_pay_overlay"
    >
      <section class="theme_copy_pay_modal">
        <h3>{{ isCollectionSuccess ? $t('text_get_results') : $t('text_no_payment_received') }}</h3>
        <p>
          {{ isCollectionSuccess ? `${collectionData.transactionNums} ${$t('text_transfer_received')}` : $t('text_check_payment_account_correct') }}
        </p>
        <div v-if="!isCollectionSuccess" class="theme_copy_pay_modal_account">
          {{ $t('text_payment_account_number') }}:<span>{{ accountNumber }}</span>
        </div>
        <div v-else class="theme_copy_pay_modal_account">
          {{ $t('text_total_amount2') }}:<span>{{ currencyCode }} {{ collectedAmountText }}</span>
        </div>
        <button
          v-if="isCollectionSuccess"
          class="theme_copy_pay_detail_btn"
          type="button"
          @click="$emit('show-detail')"
        >
          {{ $t('text_detail') }}
        </button>
        <button type="button" @click="closeCollectionModal">
          {{ $t('text_confirm2') }}
        </button>
      </section>
    </div>

    <div v-if="paymentAccountVerifyShow" class="theme_copy_pay_overlay">
      <section class="theme_copy_pay_modal">
        <h3>{{ $t('text_please_confirm_will_following_account_transfer') }}</h3>
        <p>{{ $t('text_your_payment_account') }}:</p>
        <div class="theme_copy_pay_confirm_account">
          {{ userSubmitAccountNumber || userPaymentAccount }}
        </div>
        <div class="theme_copy_pay_modal_actions">
          <button type="button" @click="$emit('return-payment-account')">
            {{ $t('text_return') }}
          </button>
          <button type="button" @click="$emit('confirm-payment-account')">
            {{ popCommitText || $t('text_confirm') }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import themeMixin from '@/mixins/theme';

export default {
  name: 'ThemeCopyPayPage',
  mixins: [themeMixin],
  props: {
    layout: {
      type: String,
      default: 'moblie',
    },
    repaymentData: {
      type: Object,
      default: () => ({}),
    },
    collectionData: {
      type: Object,
      default: () => ({}),
    },
    collectionDataStep: {
      type: Number,
      default: -1,
    },
    countryInfo: {
      type: Object,
      default: () => ({}),
    },
    countryInfoLogo: {
      type: String,
      default: '',
    },
    hr: {
      type: [String, Number],
      default: '00',
    },
    min: {
      type: [String, Number],
      default: '00',
    },
    sec: {
      type: [String, Number],
      default: '00',
    },
    formattedPaymentAmount: {
      type: [String, Number],
      default: '',
    },
    transactionIdTemplate: {
      type: String,
      default: '',
    },
    transactionId: {
      type: String,
      default: '',
    },
    isSubmitTransactionId: {
      type: Boolean,
      default: false,
    },
    refreshNum: {
      type: Number,
      default: 0,
    },
    showError: {
      type: Boolean,
      default: false,
    },
    isHideTextarea: {
      type: Boolean,
      default: false,
    },
    smsPlaceholder: {
      type: String,
      default: '',
    },
    collectionShow: {
      type: Boolean,
      default: false,
    },
    collectionDetailShow: {
      type: Boolean,
      default: false,
    },
    paymentAccountVerifyShow: {
      type: Boolean,
      default: false,
    },
    userSubmitAccountNumber: {
      type: String,
      default: '',
    },
    popCommitText: {
      type: String,
      default: '',
    },
    formatBankName: {
      type: Function,
      required: true,
    },
  },
  computed: {
    isSelfBlue() {
      return this.themeId === 'self-blue';
    },
    isCustomGold() {
      return this.themeId === 'custom-gold';
    },
    logoUrl() {
      return (
        this.repaymentData?.configData?.logoFileUrl ||
        (this.isCustomGold ? this.themeImg('payLogo') : this.countryInfoLogo) ||
        this.themeImg('logo') ||
        this.countryInfoLogo ||
        ''
      );
    },
    copyPayHero() {
      return this.themeImg('copyPayHero') || '';
    },
    copyPayCard() {
      return this.themeImg('copyPayCard') || '';
    },
    copyAmountIcon() {
      return this.themeImg('copyAmountIcon') || '';
    },
    copyAccountIcon() {
      return this.themeImg('copyAccountIcon') || '';
    },
    copyNameIcon() {
      return this.themeImg('copyNameIcon') || '';
    },
    copyWalletIcon() {
      return this.themeImg('copyWalletIcon') || '';
    },
    currencyCode() {
      return this.countryInfo?.currencyCode || '';
    },
    countryCode() {
      return this.countryInfo?.enName || '';
    },
    isThailand() {
      return this.countryCode === 'TH';
    },
    canCopyAmount() {
      return ['NE', 'MZ', 'SL', 'EG'].includes(this.countryCode);
    },
    canCopyAccountName() {
      return !['NE', 'MZ', 'SL', 'EG'].includes(this.countryCode);
    },
    cardData() {
      return this.repaymentData?.cardData || {};
    },
    cardAmountText() {
      return this.cardData.amount || '0.00';
    },
    amountText() {
      if (this.isThailand) {
        return this.cardData.discountedAmount || this.cardData.amount || '0.00';
      }
      return (
        this.cardData.amount ||
        this.repaymentData?.paymentData?.amount ||
        this.formattedPaymentAmount ||
        '0.00'
      );
    },
    payeeName() {
      const bankName = this.repaymentData?.cardData?.bankName;
      if (!bankName) return '';
      const formattedName = this.formatBankName(bankName) || bankName;
      return this.normalizeBankName(formattedName);
    },
    payeeAccountLabel() {
      return [this.payeeName, this.$t('text_account')].filter(Boolean).join(' ');
    },
    accountNumber() {
      return this.cardData.accountNumber || '--';
    },
    accountName() {
      return this.cardData.accountName || '';
    },
    bankAlias() {
      return this.cardData.bankAlias || '';
    },
    agentAccountId() {
      return this.cardData.agentAccountId || '';
    },
    cmdCode() {
      return this.cardData.cmdCode || '';
    },
    dialCode() {
      if (!this.cmdCode) return '';
      return this.replaceTemplate(
        this.cmdCode,
        this.cardData.amount,
        this.cardData.accountNumber,
        this.cardData.agentAccountId
      );
    },
    tipsContent() {
      return this.cardData.tipsContent || '';
    },
    processedTipsContent() {
      if (!this.tipsContent) return '';
      return this.replaceTemplate2(
        this.tipsContent,
        this.cardData.amount || '',
        this.cardData.accountNumber || '',
        this.cardData.agentAccountId || ''
      );
    },
    userPaymentAccount() {
      return (
        this.cardData.userSubmitAccountNumber ||
        this.repaymentData?.paymentData?.userSubmitAccountNumber ||
        this.userSubmitAccountNumber ||
        '--'
      );
    },
    cardCollectedAmountText() {
      return this.cardData.collectedAmount || 0;
    },
    collectedAmountText() {
      const amount = this.collectionData?.collectedAmount;
      if (amount === '' || amount == null) return '0.00';
      return amount;
    },
    collectDetails() {
      return this.collectionData?.collectDetails || [];
    },
    isCollectionSuccess() {
      return this.collectionDataStep === 1;
    },
    statusHelpText() {
      return this.isCollectionSuccess
        ? this.$t('text_check_successful2')
        : `${this.$t('text_click')} "${this.$t('text_refresh')}" ${this.$t('text_check_successful')}`;
    },
    showSmsInput() {
      return !this.isHideTextarea && (this.refreshNum !== 0 || !!this.transactionId);
    },
  },
  methods: {
    normalizeBankName(value) {
      const text = String(value || '').trim();
      if (!text) return '';
      return text.split('_')[0].toLocaleUpperCase();
    },
    replaceTemplate(template, a, b, c) {
      return String(template || '').replace(/amount|account|agent/g, (match) => {
        if (match === 'amount') return a || '';
        if (match === 'account') return b || '';
        if (match === 'agent') return c || '';
        return match;
      });
    },
    replaceTemplate2(template, a, b, c) {
      return String(template || '').replace(/@amount|@account|@agent/g, (match) => {
        if (match === '@amount') return a || '';
        if (match === '@account') return b || '';
        if (match === '@agent') return c || '';
        return match;
      });
    },
    displayTime(value) {
      if (value == null || value === '') return '00';
      return String(value).padStart(2, '0');
    },
    onDial() {
      if (!this.dialCode) return;
      window.location.href = `tel://${encodeURIComponent(this.dialCode)}`;
    },
    closeCollectionModal() {
      this.$emit('close-collection');
      this.$emit('close-collection-detail');
    },
  },
};
</script>

<style lang="scss" scoped>
.theme_copy_pay {
  position: relative;
  min-height: 100vh;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  background: var(--pay-color-bg-page);
  color: var(--pay-color-text-primary);
  box-sizing: border-box;
  overflow-x: hidden;
}

.theme_copy_pay--computer {
  max-width: 640px;
}

.theme_copy_pay_header {
  position: relative;
  min-height: 86px;
  padding: 22px 20px 12px;
  box-sizing: border-box;
  overflow: hidden;
}

.theme_copy_pay--self-blue .theme_copy_pay_header {
  min-height: 85px;
  padding: 29px 20px 12px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_header {
  min-height: 122px;
  padding: 22px 20px 46px;
  color: #fff;
  background: linear-gradient(-23deg, #392812 0.6%, #171716 79.5%);
  overflow: visible;
}

.theme_copy_pay_brand {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
}

.theme_copy_pay_logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: auto;
  height: 44px;
  overflow: visible;
}

.theme_copy_pay--self-blue .theme_copy_pay_logo {
  justify-content: flex-start;
  width: auto;
  height: 28px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_logo {
  width: auto;
  height: 40px;
  border-radius: 0;
  background: transparent;
}

.theme_copy_pay_logo img {
  display: block;
  width: auto;
  height: 100%;
  max-width: none;
  max-height: 100%;
  object-fit: contain;
}

.theme_copy_pay_header_hero {
  position: absolute;
  right: 4px;
  top: 12px;
  width: 86px;
  height: 98px;
  object-fit: contain;
  z-index: 2;
}

.theme_copy_pay_header_card {
  position: absolute;
  right: 24px;
  top: 55px;
  z-index: 3;
  width: 76px;
  height: 84px;
  object-fit: contain;
}

.theme_copy_pay_timer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  margin: 0 12px 11px;
  border: 1px solid var(--pay-color-border-lighter);
  border-radius: 8px;
  background: var(--pay-color-bg-card);
  box-shadow: 0 2px 8px var(--pay-color-bg-overlay);
  box-sizing: border-box;
}

.theme_copy_pay--custom-gold .theme_copy_pay_timer {
  position: relative;
  z-index: 4;
  min-height: 62px;
  margin: -36px 12px 11px;
  padding: 0 17px;
}

.theme_copy_pay--self-blue .theme_copy_pay_timer {
  position: absolute;
  top: 39px;
  right: 14px;
  width: 122px;
  margin: 0;
  padding: 0;
  border: 0;
  box-shadow: none;
}

.theme_copy_pay--self-blue .theme_copy_pay_timer span {
  position: absolute;
  top: -17px;
  left: -72px;
  right: 0;
  width: auto;
  font-size: 10px;
  color: var(--pay-color-primary-navy);
  text-align: right;
  white-space: nowrap;
}

.theme_copy_pay--self-blue.theme_copy_pay--computer .theme_copy_pay_timer {
  width: 170px;
}

.theme_copy_pay--self-blue.theme_copy_pay--computer .theme_copy_pay_timer span {
  left: 0;
}

.theme_copy_pay--custom-gold .theme_copy_pay_timer span {
  flex: 1;
  max-width: none;
  font-size: 15px;
  color: var(--pay-color-text-primary);
  white-space: nowrap;
}

.theme_copy_pay_timer span {
  font-size: 15px;
  line-height: 1.35;
}

.theme_copy_pay_timer_value {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

.theme_copy_pay_timer_value strong {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 29px;
  height: 28px;
  border: 1px solid var(--pay-color-border-gray);
  border-radius: 4px;
  background: var(--pay-color-bg-secondary);
  color: var(--pay-color-text-primary);
  font-size: 16px;
  line-height: 1;
}

.theme_copy_pay_timer_value em {
  color: var(--pay-color-text-primary);
  font-style: normal;
  font-weight: 700;
}

.theme_copy_pay_body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 12px 24px;
  box-sizing: border-box;
}

.theme_copy_pay--self-blue .theme_copy_pay_body {
  padding-top: 0;
  gap: 15px;
}

.theme_copy_pay_card {
  position: relative;
  padding: 18px 17px;
  border: 1px solid var(--pay-color-border-lighter);
  border-radius: 8px;
  background: var(--pay-color-bg-card);
  box-shadow: 0 2px 8px var(--pay-color-bg-overlay);
  overflow: hidden;
  box-sizing: border-box;
}

.theme_copy_pay_payee {
  min-height: 274px;
  padding: 34px 16px 15px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_payee {
  min-height: 288px;
  padding: 21px 19px 12px;
}

.theme_copy_pay_status {
  min-height: 194px;
  padding: 18px 16px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_status {
  min-height: 206px;
  padding: 22px 19px 16px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_status .theme_copy_pay_card_head p {
  max-width: 270px;
  margin-top: 4px;
  line-height: 16px;
}

.theme_copy_pay--computer.theme_copy_pay--custom-gold .theme_copy_pay_status .theme_copy_pay_card_head p {
  max-width: none;
}

.theme_copy_pay_wallet {
  min-height: 75px;
  padding: 15px 18px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_wallet {
  min-height: 82px;
  padding: 17px 19px;
}

.theme_copy_pay_card_head {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.theme_copy_pay_card_head > div {
  min-width: 0;
  flex: 1;
}

.theme_copy_pay_badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--pay-color-primary-light);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

.theme_copy_pay_badge_value {
  display: block;
  line-height: 1;
}

.theme_copy_pay--custom-gold .theme_copy_pay_badge {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  background: #f3a12c;
  color: #fff;
  font-size: 14px;
  line-height: 26px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_status .theme_copy_pay_badge {
  font-size: 13px;
}

.theme_copy_pay--self-blue .theme_copy_pay_payee .theme_copy_pay_badge {
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 34px;
  width: 34px;
  height: 25px;
  background: transparent;
  color: var(--pay-color-primary-bright);
  font-size: 21px;
  line-height: 1;
}

.theme_copy_pay--self-blue .theme_copy_pay_status .theme_copy_pay_badge,
.theme_copy_pay--self-blue .theme_copy_pay_wallet .theme_copy_pay_badge {
  justify-content: center;
  flex: 0 0 49px;
  width: 49px;
  height: 49px;
  background: #f1f5fe;
  color: var(--pay-color-primary-bright);
  font-size: 22px;
}

.theme_copy_pay_card h2 {
  margin: 0;
  font-size: 16px;
  line-height: 1.15;
  font-weight: 700;
  color: var(--pay-color-text-primary);
}

.theme_copy_pay_card p {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: var(--pay-color-text-muted);
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_card p b {
  color: var(--pay-color-accent-yellow-dark);
}

.theme_copy_pay_payee_hero {
  position: absolute;
  right: -1px;
  top: -21px;
  width: 112px;
  height: 92px;
  object-fit: contain;
}

.theme_copy_pay--self-blue .theme_copy_pay_payee .theme_copy_pay_card_head {
  padding-right: 112px;
  box-sizing: border-box;
}

.theme_copy_pay--self-blue .theme_copy_pay_payee .theme_copy_pay_card_head > div {
  flex: 1 1 auto;
  max-width: none;
}

.theme_copy_pay--self-blue .theme_copy_pay_payee .theme_copy_pay_card_head h2 {
  white-space: nowrap;
}

.theme_copy_pay--self-blue .theme_copy_pay_payee .theme_copy_pay_card_head p {
  max-width: none;
  overflow-wrap: normal;
  word-break: normal;
}

.theme_copy_pay--self-blue.theme_copy_pay--computer .theme_copy_pay_payee .theme_copy_pay_card_head p {
  white-space: nowrap;
}

.theme_copy_pay_account_panel {
  position: relative;
  z-index: 1;
  margin-top: 28px;
  padding: 0 0 2px;
  overflow: hidden;
}

.theme_copy_pay--self-blue .theme_copy_pay_account_panel {
  margin-top: 14px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_account_panel {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  padding: 0 18px;
  border-radius: 8px;
  background: var(--pay-color-bg-secondary);
}

.theme_copy_pay_card_deco {
  display: none;
}

.theme_copy_pay_amount {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 51px;
  padding: 0 16px;
  border-radius: 8px;
  background: var(--pay-color-primary);
  color: var(--pay-color-text-blue-light);
  overflow: hidden;
  box-sizing: border-box;
}

.theme_copy_pay_amount_text {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.theme_copy_pay--custom-gold .theme_copy_pay_amount {
  min-height: 67px;
  padding: 11px 0 12px;
  border-radius: 0;
  border-bottom: 1px solid var(--pay-color-border-gray);
  background: transparent;
  color: var(--pay-color-text-primary);
}

.theme_copy_pay--custom-gold .theme_copy_pay_amount_text {
  display: block;
}

.theme_copy_pay_amount span {
  min-width: 0;
  font-size: 12px;
  line-height: 1.2;
}

.theme_copy_pay_amount strong {
  flex: 0 0 auto;
  color: var(--pay-color-accent-yellow);
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
}

.theme_copy_pay--custom-gold .theme_copy_pay_amount strong {
  display: block;
  margin-top: 6px;
}

.theme_copy_pay_row_icon {
  display: block;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.theme_copy_pay_info_row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 41px;
  padding: 9px 10px;
  border-bottom: 1px solid var(--pay-color-border-lighter);
  box-sizing: border-box;
}

.theme_copy_pay--custom-gold .theme_copy_pay_info_row {
  order: 3;
  min-height: 67px;
  padding: 11px 0;
  border-bottom-color: var(--pay-color-border-gray);
}

.theme_copy_pay--custom-gold .theme_copy_pay_info_row--account {
  order: 1;
}

.theme_copy_pay--custom-gold .theme_copy_pay_info_row--name {
  order: 2;
  border-bottom: 0;
}

.theme_copy_pay_info_row:last-child {
  border-bottom: 0;
}

.theme_copy_pay_info_text {
  min-width: 0;
  flex: 1;
}

.theme_copy_pay_info_text span {
  display: block;
  font-size: 12px;
  line-height: 1.2;
  color: var(--pay-color-text-primary);
}

.theme_copy_pay_info_text strong {
  display: block;
  min-width: 0;
  margin-top: 6px;
  color: var(--pay-color-primary-bright);
  font-size: 18px;
  font-weight: 500;
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay--self-blue .theme_copy_pay_info_text {
  display: flex;
  align-items: center;
  gap: 9px;
}

.theme_copy_pay--self-blue .theme_copy_pay_info_text span {
  min-width: 0;
  flex: 0 0 auto;
}

.theme_copy_pay--self-blue .theme_copy_pay_info_text strong {
  flex: 1;
  min-width: 0;
  margin-top: 0;
}

.theme_copy_pay--custom-gold .theme_copy_pay_info_text strong {
  color: var(--pay-color-primary);
}

.theme_copy_pay_info_row button,
.theme_copy_pay_amount button,
.theme_copy_pay_paid_value button,
.theme_copy_pay_sms button,
.theme_copy_pay_modal button,
.theme_copy_pay_detail_btn,
.theme_copy_pay_dial_btn,
.theme_copy_pay_dial_code button,
.theme_copy_pay_result button {
  border: 0;
  border-radius: 16px;
  background: var(--pay-gradient-copy-btn);
  color: var(--pay-color-btn-text);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.theme_copy_pay_info_row button {
  flex: 0 0 auto;
  min-width: 58px;
  height: 28px;
}

.theme_copy_pay_amount button {
  flex: 0 0 auto;
  min-width: 58px;
  height: 28px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_info_row button {
  min-width: 57px;
  height: 26px;
}

.theme_copy_pay_dial_btn {
  width: 100%;
  min-height: 42px;
  margin-top: 14px;
  border-radius: 8px;
  font-size: 14px;
}

.theme_copy_pay_dial_code {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--pay-color-bg-secondary);
  color: var(--pay-color-text-primary);
  box-sizing: border-box;
}

.theme_copy_pay_dial_code span {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.35;
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_dial_code button {
  flex: 0 0 auto;
  min-width: 58px;
  height: 28px;
}

.theme_copy_pay_status .theme_copy_pay_card_head {
  align-items: center;
}

.theme_copy_pay_status .theme_copy_pay_card_head > div,
.theme_copy_pay_wallet .theme_copy_pay_card_head > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
}

.theme_copy_pay--self-blue .theme_copy_pay_status .theme_copy_pay_card_head,
.theme_copy_pay--self-blue .theme_copy_pay_wallet .theme_copy_pay_card_head {
  gap: 13px;
}

.theme_copy_pay--self-blue .theme_copy_pay_status h2,
.theme_copy_pay--self-blue .theme_copy_pay_wallet h2 {
  font-size: 15px;
  line-height: 18px;
}

.theme_copy_pay_status h2,
.theme_copy_pay_wallet h2 {
  white-space: nowrap;
}

.theme_copy_pay_status .theme_copy_pay_card_head p,
.theme_copy_pay_wallet .theme_copy_pay_card_head p {
  max-width: none;
}

.theme_copy_pay--self-blue .theme_copy_pay_status .theme_copy_pay_card_head p,
.theme_copy_pay--self-blue .theme_copy_pay_wallet .theme_copy_pay_card_head p {
  margin-top: 2px;
  max-width: none;
  font-size: 12px;
  line-height: 16px;
  color: var(--pay-color-text-muted);
}

.theme_copy_pay_paid_panel {
  margin-top: 18px;
  padding: 15px 14px;
  border: 1px solid var(--pay-color-border-lighter);
  border-radius: 8px;
  background: var(--pay-color-bg-card);
}

.theme_copy_pay--self-blue .theme_copy_pay_paid_panel {
  margin-top: 16px;
  padding: 14px 10px 13px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_paid_panel {
  margin-top: 6px;
  padding: 13px 20px 10px;
  border: 0;
  background: var(--pay-color-bg-secondary);
}

.theme_copy_pay_paid_panel > span {
  display: block;
  margin-bottom: 10px;
  font-size: 12px;
  color: var(--pay-color-text-primary);
}

.theme_copy_pay_paid_value {
  display: flex;
  align-items: center;
  gap: 10px;
}

.theme_copy_pay_paid_value strong {
  display: flex;
  align-items: center;
  flex: 1;
  min-height: 39px;
  padding: 0 14px;
  border: 1px solid var(--pay-color-border-lighter);
  border-radius: 8px;
  background: var(--pay-color-bg-stripe);
  color: var(--pay-color-text-disabled);
  font-size: 20px;
  font-weight: 600;
}

.theme_copy_pay--self-blue .theme_copy_pay_paid_value strong {
  min-height: 39px;
  font-size: 20px;
  color: #c9ceed;
}

.theme_copy_pay--custom-gold .theme_copy_pay_paid_value strong {
  min-height: 39px;
  background: #fff;
  color: #c7c3be;
}

.theme_copy_pay_status.is-success .theme_copy_pay_paid_value strong {
  color: var(--pay-color-primary);
}

.theme_copy_pay_paid_value button {
  min-width: 66px;
  height: 32px;
}

.theme_copy_pay--self-blue .theme_copy_pay_paid_value button {
  background: var(--pay-color-primary);
  color: var(--pay-color-accent-yellow);
}

.theme_copy_pay_paid_value img {
  width: 28px;
  height: 28px;
}

.theme_copy_pay_status .theme_copy_pay_paid_panel p {
  max-width: none;
  width: 100%;
  margin-top: 10px;
  color: var(--pay-color-pending-desc);
}

.theme_copy_pay--self-blue .theme_copy_pay_paid_panel p {
  max-width: none;
  margin-left: 0;
  font-size: 11px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_paid_panel p {
  max-width: none;
  margin-top: 7px;
  color: var(--pay-color-text-brown-muted);
  font-size: 9px;
  line-height: 1.25;
}

.theme_copy_pay_detail_btn {
  margin-top: 12px;
  min-width: 96px;
  height: 32px;
}

.theme_copy_pay--self-blue .theme_copy_pay_detail_btn,
.theme_copy_pay--self-blue .theme_copy_pay_result button {
  border-radius: 12px;
  background: var(--pay-color-primary);
  color: var(--pay-color-accent-yellow);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.theme_copy_pay--custom-gold .theme_copy_pay_detail_btn,
.theme_copy_pay--custom-gold .theme_copy_pay_result button {
  border-radius: 8px;
  background: var(--pay-gradient-btn-primary);
  color: var(--pay-color-btn-text);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.theme_copy_pay_sms {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--pay-color-border-lighter);
}

.theme_copy_pay_sms label,
.theme_copy_pay_sms_result span {
  display: block;
  margin-bottom: 8px;
  color: var(--pay-color-text-primary);
  font-size: 13px;
  font-weight: 600;
}

.theme_copy_pay_sms_desc,
.theme_copy_pay_sms_warn {
  margin: 0 0 8px;
  color: var(--pay-color-text-muted);
  font-size: 12px;
  line-height: 1.4;
}

.theme_copy_pay_sms_warn {
  margin: 8px 0 0;
}

.theme_copy_pay_sms_warn span {
  color: var(--pay-color-danger);
}

.theme_copy_pay_sms textarea {
  width: 100%;
  min-height: 84px;
  padding: 10px;
  border: 1px solid var(--pay-color-border-input);
  border-radius: 8px;
  background: var(--pay-color-bg-input);
  color: var(--pay-color-text-primary);
  font-size: 12px;
  line-height: 1.4;
  resize: none;
  outline: none;
  box-sizing: border-box;
}

.theme_copy_pay_sms button {
  width: 100%;
  height: 44px;
  margin-top: 12px;
  border-radius: 8px;
  background: var(--pay-gradient-btn-primary);
  color: var(--pay-color-btn-text);
  font-size: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.theme_copy_pay--self-blue .theme_copy_pay_sms button {
  height: 49px;
  border-radius: 12px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_sms button {
  height: 52px;
}

.theme_copy_pay_sms_result {
  margin-top: 10px;
}

.theme_copy_pay_sms_result strong {
  color: var(--pay-color-primary);
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_wallet .theme_copy_pay_card_head {
  align-items: center;
}

.theme_copy_pay_wallet_icon {
  flex: 0 0 45px;
  width: 45px;
  height: 45px;
  margin-left: auto;
  border-radius: 8px;
  background: var(--pay-color-bg-secondary);
  overflow: hidden;
}

.theme_copy_pay_wallet .theme_copy_pay_card_head > .theme_copy_pay_wallet_icon {
  display: block;
  flex: 0 0 45px;
  min-width: 45px;
}

.theme_copy_pay--self-blue .theme_copy_pay_wallet_icon {
  flex: 0 0 58px;
  width: 58px;
  min-width: 58px;
  height: 45px;
  background: transparent;
}

.theme_copy_pay--self-blue .theme_copy_pay_wallet .theme_copy_pay_card_head > .theme_copy_pay_wallet_icon {
  flex: 0 0 58px;
  width: 58px;
  min-width: 58px;
}

.theme_copy_pay--custom-gold .theme_copy_pay_wallet_icon {
  flex: 0 0 56px;
  width: 56px;
  min-width: 56px;
  height: 50px;
  background: transparent;
}

.theme_copy_pay--custom-gold .theme_copy_pay_wallet .theme_copy_pay_card_head > .theme_copy_pay_wallet_icon {
  flex: 0 0 56px;
  width: 56px;
  min-width: 56px;
}

.theme_copy_pay_wallet_icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.theme_copy_pay_tips h2 {
  margin: 0 0 10px;
  color: var(--pay-color-text-primary);
  font-size: 15px;
  line-height: 1.3;
}

.theme_copy_pay_tips_content {
  color: var(--pay-color-text-muted);
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_result_value {
  display: flex;
  align-items: center;
  gap: 10px;
}

.theme_copy_pay_result_value span {
  flex: 0 0 auto;
  color: var(--pay-color-text-primary);
  font-size: 12px;
}

.theme_copy_pay_result_value strong {
  flex: 1;
  min-width: 0;
  color: var(--pay-color-primary);
  font-size: 16px;
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_result button {
  flex: 0 0 auto;
  min-width: 86px;
  height: 32px;
}

.theme_copy_pay_result p {
  width: 100%;
  min-width: 0;
  margin: 10px 0 0;
  color: var(--pay-color-text-muted);
  font-size: 12px;
  line-height: 1.4;
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.35);
  box-sizing: border-box;
}

.theme_copy_pay_modal {
  width: min(327px, 100%);
  padding: 22px 18px 18px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
}

.theme_copy_pay_modal h3 {
  margin: 0;
  font-size: 18px;
  color: var(--pay-color-text-primary);
}

.theme_copy_pay_modal p {
  margin: 10px 0 16px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--pay-color-text-muted);
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_modal_account {
  margin-bottom: 12px;
  color: var(--pay-color-text-primary);
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_modal_account span {
  margin-left: 6px;
  color: var(--pay-color-primary);
  font-weight: 600;
}

.theme_copy_pay_detail,
.theme_copy_pay_confirm_account {
  padding: 12px;
  border-radius: 8px;
  background: var(--pay-color-bg-secondary);
  color: var(--pay-color-text-primary);
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_copy_pay_detail {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 12px;
}

.theme_copy_pay_detail strong {
  flex: 0 0 auto;
  color: var(--pay-color-primary);
}

.theme_copy_pay_detail_modal {
  width: min(343px, 100%);
}

.theme_copy_pay_detail_table {
  margin-top: 14px;
  overflow-x: auto;
}

.theme_copy_pay_detail_table_row {
  display: grid;
  grid-template-columns: repeat(4, minmax(86px, 1fr));
  min-width: 344px;
  background: #fff;
}

.theme_copy_pay_detail_table_row:nth-child(2n + 3) {
  background: var(--pay-color-bg-secondary);
}

.theme_copy_pay_detail_table_row span {
  min-width: 0;
  padding: 8px 6px;
  color: var(--pay-color-text-primary);
  font-size: 11px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme_copy_pay_detail_table_head span {
  color: var(--pay-color-text-muted);
  font-weight: 600;
}

.theme_copy_pay_modal > button {
  width: 100%;
  height: 42px;
  margin-top: 16px;
  border-radius: 8px;
}

.theme_copy_pay--self-blue .theme_copy_pay_modal > button {
  height: 49px;
  border-radius: 12px;
  background: var(--pay-color-primary);
  color: var(--pay-color-accent-yellow);
  font-size: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.theme_copy_pay--custom-gold .theme_copy_pay_modal > button {
  height: 52px;
  background: var(--pay-gradient-btn-primary);
  color: var(--pay-color-btn-text);
  font-size: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.theme_copy_pay_modal_actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 16px;
}

.theme_copy_pay_modal_actions button {
  height: 40px;
  border-radius: 8px;
}

.theme_copy_pay_modal_actions button:first-child {
  background: var(--pay-color-bg-secondary);
  color: var(--pay-color-text-primary);
}

.theme_copy_pay--self-blue .theme_copy_pay_modal_actions button,
.theme_copy_pay--custom-gold .theme_copy_pay_modal_actions button {
  height: 46px;
  font-size: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.theme_copy_pay--self-blue .theme_copy_pay_modal_actions button:first-child {
  border: 1px solid var(--pay-color-border-gold);
  border-radius: 12px;
  background: var(--pay-color-bg-card);
  color: var(--pay-color-primary);
}

.theme_copy_pay--self-blue .theme_copy_pay_modal_actions button:last-child {
  border-radius: 12px;
  background: var(--pay-color-primary);
  color: var(--pay-color-accent-yellow);
}

.theme_copy_pay--custom-gold .theme_copy_pay_modal_actions button:first-child {
  border: 1px solid var(--pay-color-border-accent);
  background: var(--pay-color-bg-secondary);
  color: var(--pay-color-primary);
}

.theme_copy_pay--custom-gold .theme_copy_pay_modal_actions button:last-child {
  background: var(--pay-gradient-btn-primary);
  color: var(--pay-color-btn-text);
}
</style>
